/**
 * The upgrade economy (PRD §20). Salvage is the currency; every purchase is a
 * level on one of seven modules, and every derived number the rest of the game
 * reads comes out of `statsFor` — there is no second place where a hull cap or
 * a cannon cooldown is decided.
 */

import {
  CANNON_LEVELS, NITRO, SONAR, UPGRADES,
  type CannonLevel, type UpgradeId,
} from './config';
import { SHIELD } from './config';
import { PATHFINDER, type Chassis } from './chassis';

// The shield's per-level curve. Shared across every hull, like every other
// curve here — the chassis supplies the base, this supplies the shape.
const SHIELD_PER_LEVEL = SHIELD.perLevel;
const SHIELD_REGEN_PER_LEVEL = SHIELD.regenPerLevel;
const SHIELD_DELAY_PER_LEVEL = SHIELD.delayPerLevel;

export type Levels = Record<UpgradeId, number>;

export function emptyLevels(): Levels {
  return {
    hull: 0, shield: 0, engine: 0, cannon: 0, torpedo: 0, sonar: 0, lamp: 0,
    nitro: 0, ablative: 0, warding: 0, winch: 0,
  };
}

export function defFor(id: UpgradeId) {
  return UPGRADES.find((u) => u.id === id)!;
}

export function maxLevel(id: UpgradeId): number {
  return defFor(id).costs.length;
}

/** Salvage price of the next level, or null when the module is maxed. */
export function costOf(id: UpgradeId, level: number): number | null {
  const def = defFor(id);
  return level >= def.costs.length ? null : def.costs[level];
}

/** What the *next* purchase does, for the shop line. */
export function nextBlurb(id: UpgradeId, level: number): string {
  const def = defFor(id);
  return level >= def.blurb.length ? 'MAXED' : def.blurb[level];
}

export interface Stats {
  maxHull: number;
  maxEnergy: number;
  energyRegen: number;
  /** Multiplier on collision damage — heavy plating shrugs off scrapes. */
  impactMul: number;

  maxShield: number;
  shieldRegen: number;
  shieldDelay: number;

  thrustMul: number;
  /**
   * Multiplier on terminal speed, applied as a reduction in drag.
   *
   * Separate from `thrustMul` because they are different complaints: thrust is
   * how fast the boat gets going, this is how fast it ends up. A drive that
   * only ever bought acceleration hit the same ceiling at every level.
   */
  topSpeedMul: number;
  /**
   * Divides the drag a load adds. A stronger drive does not make a crate
   * lighter — buoyancy and inertia are untouched — it makes the boat able to
   * drag it, which is what "carry more weight" actually means underwater.
   */
  haulMul: number;
  /**
   * Heaviest load the hook will take, in tonnes.
   *
   * A hard limit rather than a soft one, on purpose. Hauling was governed only
   * by drag, which is honest physics and completely illegible: a boat that
   * cannot really move a 4 t crate hooks it anyway, and the player finds out
   * over the next thirty seconds of not accelerating. A number the line refuses
   * against can be read off the shop screen before the dive instead.
   */
  liftCap: number;

  /** Multiplier on damage from explosions. Ward field. */
  blastMul: number;
  /**
   * Tint of the shell when a defence flares, and which one is loudest.
   * Deflector blue, ablative amber, ward violet — the colour is the readout.
   */
  shellTint: [number, number, number];

  nitroMax: number;
  nitroDrain: number;
  /** Refill rate while the burner is off. Scales hard at the top drive level. */
  nitroRegen: number;
  /** Multiplier on the burner's shove. */
  nitroPush: number;

  cannon: CannonLevel;
  cannonCooldown: number;
  cannonDamage: number;
  cannonEnergy: number;

  torpedoMax: number;
  torpedoDamage: number;
  torpedoBlast: number;

  chargeUnlocked: boolean;
  chargeMax: number;

  sonarRange: number;
  sonarCost: number;
  pulseOn: boolean;
  pulseDamage: number;
  pulseRadius: number;

  lampRange: number;
  lampIntensity: number;
  /** Half-angle of the cone, radians. Drives both shading and the shadow fan. */
  lampHalfAngle: number;

  /** Nothing can touch the hull. Granted by an aegis, never by a module. */
  invulnerable: boolean;
}

/**
 * Every derived stat, from the seven levels and the hull they are bolted to.
 *
 * Pure — call it whenever either changes and hand the result around; nothing
 * caches it internally.
 *
 * The split is deliberate and it is the whole reason a second boat is a
 * different *tool* rather than a bigger number: the **base** capability comes
 * from the chassis, and the **curves** live here and are shared. A level-3
 * engine therefore means the same proportional thing on every hull, while a
 * stock Dray and a stock Pathfinder have nothing in common.
 *
 * A module the chassis cannot fit is clamped to level 0 however much of one the
 * player owns. That is what makes a hull a constraint.
 */
export function statsFor(l: Levels, c: Chassis = PATHFINDER): Stats {
  const B = c.base;
  const lv = (id: UpgradeId) => (c.modules.includes(id) ? l[id] : 0);
  const hull = lv('hull'), shield = lv('shield'), engine = lv('engine');
  const cann = lv('cannon'), torp = lv('torpedo'), son = lv('sonar'), lamp = lv('lamp');
  const nit = lv('nitro'), abl = lv('ablative'), ward = lv('warding');
  const winch = lv('winch');

  const cannon = CANNON_LEVELS[Math.min(cann, CANNON_LEVELS.length - 1)];
  const lampHalf = B.lampHalfAngle * (lamp >= 2 ? 1.55 : 1);

  return {
    maxHull: B.maxHull + [0, 30, 60, 90, 130][Math.min(hull, 4)],
    maxEnergy: B.maxEnergy + son * 15,
    energyRegen: B.energyRegen + son * 2.5 + engine * 1.5,
    // Plating thins a collision; ablative armour is *for* collisions and stacks
    // on top of it.
    impactMul: (hull >= 4 ? 0.55 : hull >= 2 ? 0.78 : 1)
      * [1, 0.74, 0.52, 0.32][Math.min(abl, 3)],
    blastMul: [1, 0.76, 0.56, 0.38][Math.min(ward, 3)],
    shellTint: ward > abl && ward > 0 ? [0.62, 0.40, 1.0]
      : abl > 0 ? [1.0, 0.68, 0.30]
      : [0.34, 0.74, 1.0],

    maxShield: shield === 0 ? 0 : B.shieldBase + (shield - 1) * SHIELD_PER_LEVEL,
    shieldRegen: B.shieldRegen + Math.max(0, shield - 1) * SHIELD_REGEN_PER_LEVEL,
    shieldDelay: Math.max(1.4, B.shieldDelay + Math.max(0, shield - 1) * SHIELD_DELAY_PER_LEVEL),

    thrustMul: 1 + engine * 0.13,
    // Terminal speed is thrust over drag, so this is spent on the denominator:
    // levels 4 and 5 are where the drive stops being an acceleration upgrade.
    topSpeedMul: 1 + Math.max(0, engine - 3) * 0.16,
    // A load's drag is what actually costs top speed, so this is the term a
    // hauling drive divides. At level 5 a 2 t crate drags like 1.2 t did.
    // MOVED OFF THE DRIVE AND ONTO THE WINCH. It keyed off engine levels 4 and
    // 5, so the only route to hauling was buying the two most expensive speed
    // levels in the game and hoping. Same shape of curve, its own module.
    haulMul: 1 / (1 + winch * 0.30),
    // The chassis's own rating, geared up. The multipliers track the blurbs,
    // which are the numbers the player is actually shown.
    liftCap: c.base.liftCap * [1, 1.6, 2.4, 3.3, 4.7][Math.min(winch, 4)]!,
    // THE LAST DRIVE LEVEL IS A DIFFERENT BOAT.
    //
    // A flat +30 a level made the 640-salvage purchase the least exciting thing
    // in the shop: 160 to 190 is a rounding error you cannot feel, on the most
    // expensive module in the game. The top level now roughly doubles what the
    // second one gave you and, more importantly, refills — regen was a flat 3.4
    // at every level, so a full tank took 56 seconds however much you had paid.
    //
    // Fast regen cannot run away with it, because regen only ticks while the
    // burner is OFF: 18 a second out against 19.4 a second back is a boat that
    // can burn half the time, not one with infinite nitro.
    // The burner is its own module now. `engine` still supplies the tank it was
    // always tied to, so an existing save keeps exactly what it paid for, and
    // `nitro` layers on top of it.
    nitroMax: B.nitroMax + engine * 30 + (engine >= 3 ? 90 : 0) + nit * 55,
    nitroDrain: B.nitroDrain * (engine >= 3 ? 0.6 : 1)
      * (nit >= 2 ? 0.78 : 1) * (nit >= 4 ? 1.25 : 1),
    nitroRegen: NITRO.regen * (1 + engine * 0.4) * (engine >= 3 ? 2.6 : 1)
      * (1 + nit * 0.35),
    // Levels 3 and 4 are the ones you feel: a harder shove, and at 4 a much
    // harder one bought with a thirstier burn above.
    nitroPush: 1 + (nit >= 3 ? 0.22 : 0) + (nit >= 4 ? 0.3 : 0),

    cannon,
    cannonCooldown: B.cannonCooldown * cannon.rate,
    cannonDamage: B.cannonDamage * cannon.damage,
    cannonEnergy: B.cannonEnergy * cannon.energy,

    torpedoMax: B.torpedoMax + torp * 4,
    torpedoDamage: B.torpedoDamage * (1 + torp * 0.22),
    torpedoBlast: B.torpedoBlast * (1 + torp * 0.15),

    // A rack the hull does not have cannot be unlocked by a module level.
    chargeUnlocked: torp >= 3 && B.chargeMax > 0,
    chargeMax: B.chargeMax,

    sonarRange: B.sonarRange * (1 + son * 0.2),
    sonarCost: B.sonarCost * (son >= 1 ? 0.72 : 1),
    pulseOn: son >= SONAR.pulseLevel,
    pulseDamage: SONAR.pulseDamage * (son >= 3 ? 1.7 : 1),
    pulseRadius: SONAR.pulseRadius * (son >= 3 ? 1.45 : 1),

    lampRange: B.lampRange * (1 + lamp * 0.28),
    lampIntensity: B.lampIntensity * (1 + lamp * 0.3),
    lampHalfAngle: lampHalf,

    invulnerable: false,
  };
}

/** Cosine pair the shader wants for a cone of this half-angle. */
export function coneCosines(half: number): { inner: number; outer: number } {
  return { inner: Math.cos(half * 0.44), outer: Math.cos(half) };
}


// ---------------------------------------------------------------------------
// Modifiers
//
// One mechanism for every temporary change to a stat, so that Survival's
// run-scoped upgrades, the timed powerups that already exist and anything the
// hold does to the boat are the same kind of thing rather than three.
//
// The critical property is the empty case: with no modifiers the resolve
// returns the base object *by reference*, untouched. `statsFor` output has to
// survive being handed through this without a single float being recomputed,
// because `npm run trace` compares the integrator against a baseline taken
// before any of this existed.
// ---------------------------------------------------------------------------

export type StatKey =
  | 'maxHull' | 'maxEnergy' | 'energyRegen' | 'impactMul'
  | 'maxShield' | 'shieldRegen' | 'shieldDelay'
  | 'thrustMul' | 'nitroMax' | 'nitroDrain'
  | 'cannonCooldown' | 'cannonDamage' | 'cannonEnergy'
  | 'torpedoMax' | 'torpedoDamage' | 'torpedoBlast' | 'chargeMax'
  | 'sonarRange' | 'sonarCost' | 'pulseDamage' | 'pulseRadius'
  | 'lampRange' | 'lampIntensity' | 'lampHalfAngle';

export type FlagKey = 'chargeUnlocked' | 'pulseOn' | 'invulnerable';

/** Where a modifier came from, so a whole class can be dropped at once. */
export type ModSource = 'timed' | 'run' | 'cargo';

export interface Modifier {
  id: string;
  source: ModSource;
  /** Seconds left. `Infinity` for anything not on a clock. */
  time: number;
  add?: Partial<Record<StatKey, number>>;
  mul?: Partial<Record<StatKey, number>>;
  set?: Partial<Record<StatKey, number>>;
  flag?: Partial<Record<FlagKey, boolean>>;
}

/** Every `add`, then every `mul`, then every `set`. Order is the contract. */
export function resolveStats(base: Stats, mods: readonly Modifier[]): Stats {
  if (!mods.length) return base;
  const out: Stats = { ...base };
  for (const m of mods) {
    if (!m.add) continue;
    for (const k in m.add) out[k as StatKey] += m.add[k as StatKey]!;
  }
  for (const m of mods) {
    if (!m.mul) continue;
    for (const k in m.mul) out[k as StatKey] *= m.mul[k as StatKey]!;
  }
  for (const m of mods) {
    if (m.set) for (const k in m.set) out[k as StatKey] = m.set[k as StatKey]!;
    if (m.flag) for (const k in m.flag) out[k as FlagKey] = m.flag[k as FlagKey]!;
  }
  return out;
}

export class ModifierStack {
  private mods: Modifier[] = [];
  /** Set whenever the set changes, so the owner knows to re-resolve. */
  dirty = false;

  get list(): readonly Modifier[] { return this.mods; }
  get active(): number { return this.mods.length; }

  /** Add, or refresh an existing one of the same id rather than stacking it. */
  grant(m: Modifier) {
    const at = this.mods.findIndex((q) => q.id === m.id);
    if (at >= 0) this.mods[at] = { ...m, time: Math.max(this.mods[at].time, m.time) };
    else this.mods.push(m);
    this.dirty = true;
  }

  /** Seconds left on one modifier, or 0. What the HUD's timer rings read. */
  timeOf(id: string): number {
    const m = this.mods.find((q) => q.id === id);
    return m ? m.time : 0;
  }

  has(id: string): boolean { return this.mods.some((q) => q.id === id); }

  clear(source?: ModSource) {
    const before = this.mods.length;
    this.mods = source ? this.mods.filter((m) => m.source !== source) : [];
    if (this.mods.length !== before) this.dirty = true;
  }

  /** Tick the clocks. Returns true when the resolved stats need rebuilding. */
  update(dt: number): boolean {
    if (this.mods.length) {
      for (let i = this.mods.length - 1; i >= 0; i--) {
        const m = this.mods[i];
        if (m.time === Infinity) continue;
        m.time -= dt;
        if (m.time <= 0) { this.mods.splice(i, 1); this.dirty = true; }
      }
    }
    const d = this.dirty;
    this.dirty = false;
    return d;
  }
}
